import { FormEvent, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMedicinesCatalog } from '../../medicines/context/MedicinesCatalogContext';
import type { Medicine } from '../../medicines/types/medicine';
import { useOrdersCatalog } from '../context/OrdersCatalogContext';
import type { OrderLine } from '../types/order';
import { newLineId } from '../types/order';

export default function CreateOrderPage() {
  const navigate = useNavigate();
  const { medicines } = useMedicinesCatalog();
  const { addOrder } = useOrdersCatalog();

  const [clientDni, setClientDni] = useState('');
  const [clientName, setClientName] = useState('');
  const [address, setAddress] = useState('');
  const [reference, setReference] = useState('');
  const [lines, setLines] = useState<OrderLine[]>([]);

  const [lineModalOpen, setLineModalOpen] = useState(false);
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMedicineId, setSelectedMedicineId] = useState<number | null>(null);
  const [lineQuantity, setLineQuantity] = useState(1);

  const selectedMedicine = useMemo(
    () => medicines.find((m) => m.id === selectedMedicineId) ?? null,
    [medicines, selectedMedicineId]
  );

  const filteredMedicines = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) {
      return medicines;
    }
    return medicines.filter((m) => m.name.toLowerCase().includes(q));
  }, [medicines, searchQuery]);

  const lineSubtotal = useMemo(() => {
    if (!selectedMedicine) {
      return 0;
    }
    const qty = Math.min(Math.max(1, lineQuantity), selectedMedicine.quantity);
    return qty * selectedMedicine.unitPrice;
  }, [selectedMedicine, lineQuantity]);

  const orderGrandTotal = useMemo(() => lines.reduce((sum, line) => sum + line.total, 0), [lines]);

  const openAddLineModal = () => {
    setEditingLineId(null);
    setSearchQuery('');
    setSelectedMedicineId(null);
    setLineQuantity(1);
    setLineModalOpen(true);
  };

  const openEditLineModal = (line: OrderLine) => {
    setEditingLineId(line.id);
    setSearchQuery('');
    setSelectedMedicineId(line.medicineId);
    setLineQuantity(line.quantity);
    setLineModalOpen(true);
  };

  const closeLineModal = () => {
    setLineModalOpen(false);
    setEditingLineId(null);
    setSelectedMedicineId(null);
  };

  const handleSelectMedicine = (medicine: Medicine) => {
    setSelectedMedicineId(medicine.id);
    setLineQuantity(1);
  };

  const handleAddOrUpdateLine = () => {
    if (!selectedMedicine) {
      return;
    }

    const maxQty = selectedMedicine.quantity;
    const qty = Math.min(Math.max(1, Math.floor(lineQuantity)), maxQty);
    if (qty < 1) {
      return;
    }

    const total = qty * selectedMedicine.unitPrice;

    if (editingLineId) {
      setLines((prev) =>
        prev.map((line) =>
          line.id === editingLineId
            ? {
                ...line,
                medicineId: selectedMedicine.id,
                productName: selectedMedicine.name,
                quantity: qty,
                unitPrice: selectedMedicine.unitPrice,
                total,
              }
            : line
        )
      );
      closeLineModal();
      return;
    }

    const duplicate = lines.some((line) => line.medicineId === selectedMedicine.id);
    if (duplicate) {
      return;
    }

    setLines((prev) => [
      ...prev,
      {
        id: newLineId(),
        medicineId: selectedMedicine.id,
        productName: selectedMedicine.name,
        quantity: qty,
        unitPrice: selectedMedicine.unitPrice,
        total,
      },
    ]);
    closeLineModal();
  };

  const removeLine = (id: string) => {
    setLines((prev) => prev.filter((line) => line.id !== id));
  };

  const handleSubmitOrder = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!clientName.trim() || !address.trim() || !reference.trim()) {
      return;
    }
    if (lines.length === 0) {
      return;
    }

    addOrder({
      clientDni: clientDni.trim(),
      clientName: clientName.trim(),
      address: address.trim(),
      reference: reference.trim(),
      lines,
      total: orderGrandTotal,
    });

    navigate('/admin/ordenes');
  };

  const duplicateMedicineInOrder =
    editingLineId === null && selectedMedicine !== null && lines.some((line) => line.medicineId === selectedMedicine.id);

  const canConfirmLine = useMemo(() => {
    if (!selectedMedicine || duplicateMedicineInOrder) {
      return false;
    }
    const qty = Math.floor(Number(lineQuantity));
    if (!Number.isFinite(qty) || qty < 1) {
      return false;
    }
    return qty <= selectedMedicine.quantity;
  }, [selectedMedicine, lineQuantity, duplicateMedicineInOrder]);

  return (
    <section className="card content-card">
      <header className="module-header">
        <h2>Crear pedido</h2>
        <p className="subtitle">Completa los datos del cliente y las lineas del pedido.</p>
        <Link to="/admin/ordenes" className="back-link">
          Volver a ordenes
        </Link>
      </header>

      <form onSubmit={handleSubmitOrder}>
        <div className="card-section order-client-section">
          <h3 className="section-title">Datos del cliente</h3>
          <div className="order-form-grid">
            <div className="field-group field-row-half">
              <label htmlFor="client-dni">DNI Cliente (opcional)</label>
              <input
                id="client-dni"
                type="text"
                placeholder="Ej. 12345678"
                value={clientDni}
                onChange={(e) => setClientDni(e.target.value)}
              />
            </div>
            <div className="field-group field-row-half">
              <label htmlFor="client-name">Nombre cliente (requerido)</label>
              <input
                id="client-name"
                type="text"
                placeholder="Nombre completo"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                required
              />
            </div>
            <div className="field-group field-row-full">
              <label htmlFor="client-address">Direccion completa (requerido)</label>
              <textarea
                id="client-address"
                className="textarea-field"
                rows={3}
                placeholder="Calle, numero, distrito, referencias de llegada..."
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                required
              />
            </div>
            <div className="field-group field-row-full">
              <label htmlFor="client-reference">Referencia (requerido)</label>
              <textarea
                id="client-reference"
                className="textarea-field"
                rows={2}
                placeholder="Ej. Frente al parque, porton azul"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                required
              />
            </div>
          </div>
        </div>

        <div className="table-wrapper card-section">
          <div className="table-header">
            <h3 className="section-title">Pedido</h3>
            <button type="button" className="add-medicine-button" onClick={openAddLineModal}>
              Agregar medicina
            </button>
          </div>
          <table className="medicine-table">
            <thead>
              <tr>
                <th>Nombre producto</th>
                <th>Cantidad</th>
                <th>Total</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.id}>
                  <td>{line.productName}</td>
                  <td>{line.quantity}</td>
                  <td>S/. {line.total.toFixed(2)}</td>
                  <td className="table-actions-cell">
                    <button type="button" className="table-action-button" onClick={() => openEditLineModal(line)}>
                      Editar
                    </button>
                    <button
                      type="button"
                      className="table-action-button table-action-button-danger"
                      onClick={() => removeLine(line.id)}
                    >
                      Quitar
                    </button>
                  </td>
                </tr>
              ))}
              {lines.length === 0 ? (
                <tr>
                  <td colSpan={4} className="empty-row">
                    No hay lineas en el pedido. Usa &quot;Agregar medicina&quot;.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          {lines.length > 0 ? (
            <div className="order-total-row">
              <strong>Total del pedido:</strong>
              <span>S/. {orderGrandTotal.toFixed(2)}</span>
            </div>
          ) : null}
        </div>

        <div className="order-form-actions">
          <button type="submit" disabled={lines.length === 0}>
            Registrar orden
          </button>
        </div>
      </form>

      {lineModalOpen ? (
        <div className="modal-overlay" onClick={closeLineModal} role="presentation">
          <div className="modal-content modal-content-lg" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="modal-header">
              <h3>{editingLineId ? 'Editar linea del pedido' : 'Agregar medicina al pedido'}</h3>
              <button type="button" className="modal-close" onClick={closeLineModal}>
                x
              </button>
            </div>

            <div className="medicine-form">
              {editingLineId ? (
                <div className="field-group field-row-full">
                  <label>Medicina</label>
                  <div className="order-readonly-value order-readonly-block">{selectedMedicine?.name ?? '—'}</div>
                  <p className="subtitle order-modal-hint">Ajusta la cantidad. No puede superar el stock del catalogo.</p>
                </div>
              ) : (
                <>
                  <div className="field-group field-row-full">
                    <label htmlFor="medicine-search">Buscar medicina</label>
                    <input
                      id="medicine-search"
                      type="text"
                      placeholder="Escribe para filtrar por nombre..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>

                  <div className="medicine-picker-list">
                    {filteredMedicines.map((medicine) => (
                      <button
                        key={medicine.id}
                        type="button"
                        className={`medicine-picker-item ${selectedMedicineId === medicine.id ? 'is-selected' : ''}`}
                        onClick={() => handleSelectMedicine(medicine)}
                      >
                        <span className="medicine-picker-name">{medicine.name}</span>
                        <span className="medicine-picker-meta">
                          Stock: {medicine.quantity} · S/. {medicine.unitPrice.toFixed(2)} c/u
                        </span>
                      </button>
                    ))}
                    {filteredMedicines.length === 0 ? <p className="subtitle">No hay medicinas que coincidan.</p> : null}
                  </div>
                </>
              )}

              {selectedMedicine ? (
                <>
                  <div className="order-form-grid order-modal-qty-row">
                    <div className="field-group field-row-half">
                      <label htmlFor="line-qty">Cantidad (max. {selectedMedicine.quantity})</label>
                      <input
                        id="line-qty"
                        type="number"
                        min={1}
                        max={selectedMedicine.quantity}
                        value={lineQuantity}
                        onChange={(e) => {
                          const next = Number(e.target.value);
                          setLineQuantity(Number.isFinite(next) ? next : 1);
                        }}
                      />
                    </div>
                    <div className="field-group field-row-half">
                      <label>Precio unitario</label>
                      <div className="order-readonly-value">S/. {selectedMedicine.unitPrice.toFixed(2)}</div>
                    </div>
                  </div>
                  <div className="order-line-subtotal">
                    <span>Subtotal linea</span>
                    <strong>S/. {lineSubtotal.toFixed(2)}</strong>
                  </div>
                </>
              ) : (
                <p className="subtitle">Selecciona una medicina de la lista.</p>
              )}

              {duplicateMedicineInOrder ? (
                <p className="error">Esta medicina ya esta en el pedido. Edita la linea existente o elige otra.</p>
              ) : null}

              <div className="medicine-form-actions">
                <button type="button" className="secondary-button" onClick={closeLineModal}>
                  Cancelar
                </button>
                <button type="button" onClick={handleAddOrUpdateLine} disabled={!canConfirmLine}>
                  {editingLineId ? 'Guardar' : 'Agregar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
